import { or, and, not, createMachine } from 'xstate';

import { routePath } from 'config/route-config';
import { actionUrl } from 'config/static-content';
import { events } from 'machines/config/events';
import { guards } from 'machines/config/guards';
import { stringToBool } from 'utils/misc';

export const quoteByEmailMachineConfigRevamp = {
  id: 'quoteByEmailMachine',
  context: {
    guards: {}
  },
  initial: 'home',
  states: {
    home: {
      on: {
        NEXT: {
          target: 'quote'
        },
        QUOTE_EXPIRED: {
          target: 'quoteExpired'
        },
        BLOCK_JOURNEY: {
          target: 'businessUpdatesAnnouncement'
        }
      },
      meta: {
        path: routePath.retrieveQuote
      }
    },

    businessUpdatesAnnouncement: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmail'
        },
        ALLOW_JOURNEY: {
          target: 'home'
        }
      },

      meta: {
        path: routePath.businessUpdatesAnnouncement
      }
    },
    businessUpdatesSubmitEmail: {
      on: {
        NEXT: {
          target: 'businessUpdatesSubmitEmailSuccess'
        }
      },
      meta: {
        path: routePath.businessUpdatesSubmitEmail
      }
    },

    businessUpdatesSubmitEmailSuccess: {
      meta: {
        path: routePath.businessUpdatesSubmitEmailSuccess
      }
    },

    quoteExpired: {
      on: {
        NEXT: [
          {
            target: 'energyNeeds',
            guard: 'hasAnyMeterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ]
      },
      meta: {
        path: routePath.quoteExpired
      }
    },

    meterpointValidation: {
      on: {
        NEXT: [
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'energyNeeds'
          }
        ],
        GUIDE_CLICK: {
          target: 'meterPointGuide'
        }
      },
      meta: {
        path: routePath.meterpointValidation
      }
    },

    meterPointGuide: {
      meta: {
        path: routePath.meterpointGuide
      }
    },

    selectOptions: {
      on: {
        ELIGIBLE_FUEL: {
          target: 'energyNeeds'
        },
        EDIT_ADDRESS: {
          target: 'home'
        },
        HOME_MOVE: [
          {
            target: 'externalContactUs',
            description: `or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])`,

            guard: or([
              'isMixedNepSap',
              and([not('allowJourneyMoveIn'), 'isBGECustomer'])
            ])
          },
          {
            target: 'externalMoveIn',
            guard: 'isBGECustomer'
          },
          {
            target: 'externalBgClassicHomeMove'
          }
        ],
        MIXED_METERS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.selectOptions
      }
    },

    energyNeeds: {
      on: {
        NEXT: [
          {
            target: 'meterpointValidation',
            guard: 'meterValidationRequired'
          },
          {
            target: 'selectOptions',
            description: `or(['hasMeterExceptions', 'hasMixedMeters'])`,
            guard: or(['hasMeterExceptions', 'hasMixedMeters'])
          },
          {
            target: 'evTariffInformation',
            guard: 'showEvTariffInformation'
          },
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.energyNeeds
      }
    },

    evTariffInformation: {
      on: {
        NEXT: [
          {
            target: 'quote',
            guard: 'isEligibleCustomer'
          },
          {
            target: 'personalInformationSummary'
          }
        ]
      },
      meta: {
        path: routePath.evTariffInformation
      }
    },

    personalInformationSummary: {
      on: {
        NEXT: {
          target: 'externalBgSalesApp'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.personalInformationSummary
      }
    },

    quote: {
      on: {
        NEXT: {
          target: 'personalDetails'
        },
        EMAIL_QUOTE: {
          target: 'emailQuote'
        },
        CONSUMPTION: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.quote
      }
    },

    emailQuote: {
      on: {
        NEXT: {
          target: 'quote'
        }
      },
      meta: {
        path: routePath.emailQuote
      }
    },

    personalDetails: {
      on: {
        NEXT: [
          {
            target: 'priorityServicesRegister',
            guard: 'hasPSR'
          },
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.personalDetails
      }
    },

    priorityServicesRegister: {
      on: {
        NEXT: [
          {
            target: 'directDebit',
            guard: 'isAnyDirectDebit'
          },
          {
            target: 'orderSummary'
          }
        ]
      },
      meta: {
        path: routePath.psrEntry
      }
    },

    directDebit: {
      on: {
        NEXT: {
          target: 'orderSummary'
        }
      },

      meta: {
        path: routePath.directDebit
      }
    },

    orderSummary: {
      on: {
        NEXT: {
          target: 'orderComplete'
        },
        CHANGE_PERSONAL_DETAILS: {
          target: 'personalDetails'
        },
        CHANGE_ADDRESS: {
          target: 'home'
        },
        CHANGE_PAYMENT_DETAILS: {
          target: 'directDebit'
        },
        CHANGE_PSR_DETAILS: {
          target: 'priorityServicesRegister'
        },
        CHANGE_ENERGY_NEEDS: {
          target: 'energyNeeds'
        }
      },
      meta: {
        path: routePath.orderSummary
      }
    },

    orderComplete: {
      meta: {
        path: routePath.orderComplete
      }
    },

    externalBgSalesApp: {
      meta: {
        path: actionUrl.bgSalesApp
      }
    },

    externalContactUs: {
      meta: { path: actionUrl.contact }
    },
    externalBgClassicHomeMove: {
      meta: { path: actionUrl.bgClassicHomeMoveUrl }
    },

    externalMoveIn: {
      meta: { path: actionUrl.moveIn }
    }
  },
  ...events
};

const { VITE_XSTATE_DEBUG } = process.env;
const debug = stringToBool(VITE_XSTATE_DEBUG);
// for VS extesnion
/* istanbul ignore next -- @preserve */
debug &&
  createMachine(
    {
      /** @xstate-layout N4IgpgJg5mDOIC5QEcCuB7ALmAQgTwFEBbAQwEsAbAWRIGMALMgOzADp70iwBiAOQIAaAFQDaABgC6iUAAd0sMpjLom0kAA9EAJjEBOAKysALAHYAbAGYjW3SYAcWu6YA0IPIgCM+sWNZmPpgEGFrqWAL5hrmhYuISklDQMzGwcXNwAigCqAPJCBAD6ggAKAJIASgQAIuJSSCByCkoqapoIXiZarPbm-vreZmZ2uq7ubfoenRYT-iYmBmJGFhFRGNj4xOTUdIws7Jw8OAAy2QDCANL5AFLZmWX8AJo1ag2Kyqp1rToGxuZWNvaOFxuTxGbxdIYBLQ2OxiCz6ZYgaJrOKbRI7NgAI1QChYsFgmRkEBI2FgAEEmEx0KgmLQwFwmJg+IJRJJnvJXs0PtozFoPKxQiYPAYTBYLP4LCNPKELMYzPojEYzLo9Lo7HYTAikbENgltslWFicXB8YTiXByZTqbT6YzSYdjgB1K43O4ER6suovJrvUCfPSGUyWay2BxOEyStpGHz8yz6JVmaweMQayKI1ba+JbJK7Q3JPEEokkgDKqAxREUOooTOET097O9LUQUwsJlYdg8cMVs2syfDwLa0tl8sVyt0qvVmvT60zaP1WoI6hkZAATpBqyzarJ629GwhRa2OwMRSZQR52wsIzo4X4ox4hXY4VMUysYtPUXrdvPFyu1-wax5N3qbdOV9JsW1YQ8zGPU9zyMS8xDsAM5QVJUVTVZ801fFFdWzNgvyXVcIHXEQtEAr0dy5PdwMg6Dxlgy8O0MGwkyFON9F0Dw5UnLDK1nT90wXAjf2ZEQLDI4CfQ0MCDzFKCWxgpM4P7XlEIgzj2KhFSFQwrU3xw9FWAAM1QMAKCEPAZB4P8NzZRoKNAvcYS6OMbCfQYLC0CV+1Q1g4RseNTFsLQdKnbCswM4zTPMyziIA2yOUk1plRlIxlUQowzyTPp9AjO9xlYLRrDhHwxD6dTuORXiPzYSKzIsqyRNI+KG0o5LjDS+VMtKvoIzsMxfHYjtHEsM8EzMCqM3fXCjJMuqYpOAAJUleAAcQKUlKkqCoiyLWstzskCpIQNrUoQzrz2yiMON8DLUImdU5i0cbU10sK+LYLhsGXORmEwAA1EgKDIQs3mIj19oS3d9wg2TaK6pTRgmQU-DK1ULDELR9AcEKeJnarWE+sBvvQX6AaBkGVFi8SDsS6SYaPeS6MUiMT18Dx7HlTzYWVZMJr08L9UJ4nScB4HiVB6ySOpyHKOhmjGfhiMEwG4L9C+dHCrVPm3vxoWfoZMmxe9bgVsyEpKgKE5DhKc49qAmmoeo2GFfo-sOw4tsxwMeVSscDztaq6bYFMsBaEwbIZG9WBuAIa2VpKI4CgAMUyWO7fIw7Wg7WZWCxrGrB6YKzFyxVOiMdUFnGEI8qWF7QsDgzg4oUPw8jt5o6qEohHyDatoIHb04k3ds9bPOH0DHlzFy9nOh8RUFlsQM1YDvGg5DsOI6j7gFuyKgCiobI-oIQeHcokfc8Q8fC6nt2-gggY5ShDshUGFepsb9fW63ne9-yA+j5EHFOsp8HLnzHgXAYRdcrBT5HKNWg1CpJiGG-fS+om4t03u3beu996H2Pk1YBMtQEtlHpfCBk9i5uyfm2GwugPIL0WDjSqq8DLlnUJAKgYAvrR0luDe2RCjrNlbO2TsUETw6FmCXZUfgnC8gMMFAETDJqoN2JZZcsAVCi2DukdMYNpYtQci2UqvkQjDigrCQUfZEblyYoFFs6ooRFRQQLVRRMNFMC0WAHRMQqbNXsoIkwxjRQGBQoEls7NcryhlMecYjgxycVVM496rA1HuM8d47AxECEQwMQEoJpjQkWIibfBMbY-InlFKdKCST8ZgAAG5CBIMuMghlDIlCYIZdAy5SDG14fo-xrRrD2D8B0ZUfRkypQ8CXVShU5j2I6IVJ6NTpoyBIHgG0nDMAcCIn0vxmdEBDLsCMmw3UJkcUiYsLoLZ9Anj6tjTiyyDK0BULAVARA24qAIAyZceA9F7NpggQ5xyxneBPOct29gjC50sGdbKehzCPP1M8pgrz3nei+ZgH5vjCG5MGR5I5Xh2ZwgGA+dsvU+QeRsP8EU7MOx2ERa49RmiKDtM6d08WKgSxEFIFi3ZOKBl03lieJmF5+zhLbGIfqoJEK2ATAytgqTmWsq6T0t4XKeW-MWstNaPdNrbV2nwjOAK5bO2FYrfsPs2y-BMHGM8IQll11xu-fUiqPEso6SqjlTB1VNN+SnWO+QhD3CKMfQ1Q9ZZOwZma12ox2K+ECUKcuwVFg6Gei+ZhzrGVpPdWy1VnK3kau4FQEoAgqh-wIHkMoBr+n7KojJKNClRWjEmawUqCEMahEKgmeljqM0qIVW4pVHr2Xeh9VikNVbsi8DtCUIsBQsi5FDTW41ka5LRuZspfKtrHDqhuZKjySj+bJNdYDZVI61UFt9dwLVq1LZTqLJkKgRQhAlCnSfARrQTUNpFQjbQYg+QihuZxFijF4S9uUS4vCui+U5IFQgJ6vIYyCmFKKcUuVlRQtsFjQUap0a9HlawLUMcqCkhKIcfIC68jvtxdyRDApWIijFB2Eu4FZg8mup5RwZ4CNEZOPex9z7X28Go3BhDfJ6MoaY15RGAwoVeA7boKEgpRRgfTRB5JRH-XkaDSGkTtbeSgggoE1ydKxwilyrCXwDgDC6EVDyJMcIeO6OLaWyo5bK3Vv+buMTSGGOoeY27VKhhYTNjHJYWYDzwNHvxkRidRYp0zrnRRnIVGw0gKOj5iTNz-PSc8FMcTqUOIdDhB2AjmAmktMMpULhmweEiTSx+ps2c2yMS7OI3s6GFj8hteqM8D5JVeAI3STYGSGo1gazR+DJ4oUWKrn5WzXhcoWHVPyUFdq5L6DFAR2gq4zSkloM86kjIYP8Mm0YwwwSzFhMsehmwbZL5QXbIEbbu3sD7cOwybFsHa3nZMSErsRSrF-ptV0UIcoHCmHMJFtT0Xpo7bAHtg7VJPuS2yaduDv3LuFPCUD+DfWYx5SRnMVKtcYc6zh69sA73keMhvTq3u+q9PGuayI+UYieySOUghGU6oHCik8nMTbBGIA-jDtVjEihopjZsvy2tQKoInPGWCqZbtva+QfMqE5ixy5pswn2yDrARerjF2ACXx36vLt3KYBwud2Js+TC2Sw8FBi53LqYuhoIxSqb1+p-GMhYDLgxbyi3XnKLeH-V0SV-Vls8j0L++DkrWyCn6E9f9V4CP+8D9835ksgHfYBeHgDUfYR9S+PHlSBKFF9VCD4LiUXycGUzycEgkdUCrj+bLgvnYTELEld7PqlDRipqT-Jnk-UJiwgI9iImRRVyGSJmAGkcAO-593HGKYEFVZQn3HMBiTl1Te3X+7BCBGukQCJmOnPIfO9r6GGYCCqoiuPbvHvqzgu+gjRCCfhElJz-wDqK9A3MkKHg5AALSD6IDgGtolQwGwGIQEapBgAgFHRCgR59RTALAT4QgQGRi2D8j7iKY9aDA8gEa5i4gmiFjmgUjI7WiL6YDIGfAYEFTTYcTthBBNpSiWB+CVJQ5+TLz15AE5jYh5gUFmiwAlhlgViZgMHaAD5lIMIKhPhaDoZcGWAQKMSYxaCkHCHkEFhiESHliYCVglgHbGgyF1qdCs6dREpni44vwyhqGBgaH8Fk6CFQYxCCQ-gQDmHAYFTLZQgD4hh9S446APgwzqF8GeQEa1RS7mHHj3xiiYxDBiiSo5RuwYytjdoJgBAOBFwEZ6wkwGyiwUySRGrDzGKC6MKVyBJDDKH9jxFOByhJiBK-Dtj5FcIz6FGYArSoDAxIE36URKjiYihOCUoYwLA4HuyzxQSKZPT+AKjlwEboIbwfIoo+G8idChGdgwhfCKbQLIx84LDVxxgcS6D5FkDsIQCbJuLmFxK+BQiWByhQTlwYx2BSJWZqyjT2AhAKgZ6DpupkDaLpjmFWCcTMGkrShqxKjx45EBheBODyjDibY9quEsL6j1KNLNKtJnp5qlHhoORRiqQDDeDqioQcRWDTJwnsTmJYaSoZ5rIbJcLbLmFRiKbOSpQkqjg2ppGIxRL8ilTlwdgdq8wCFom7DIqoqrFB6jCr6USgi2ZdD9TJiSrb6qy5TmAHgdBs7BAqbe6AFikDpMpuo4leqX4sl1GjBii6A-CmJWAZRYZ6n1wGmEbAkDEOSFRYy5wdpCioZsYwlBYFT-q9j+EGBtGimZpsDlZYlVY1aUD-6ynul0K+D-qsn2AGCyYWlSjGIBDLZRjDRzDILhn9qsDDaUCja3FyH9apQLFKEsaTBj5akcyLAvYI5vZI5HYgmmAyjyiPTyh0KBIcEJ4g6hDylxgDC2Yok+6w4GRG4tzi6S71Qslx7q4grLYcSzLoZ9C262FCkwh9TC6i6YDzn0FulHSKhJh+AC7Pw1EhDO5HKCiPGLD+HHgZ4B7SkslqgXbwK5GBjl6ZT3YIS2CEr+ivnLjN6t6rjmGfFHJTCmA+AT6II8l-pQT3bowYEMJT7BzLiz5gDz6rhL7xno61qIkHieSKETCLxwh758gLA2qFSwixJOCn7Ljn7LhmmnmtDZYyiKbdSIR3hHjUWtqmBqxRglaAjMWsUnCcAyDNzYBQVjhHJjhQj-odAa5-nthCV0WiWMVGBDbqBfRuo4BQBFiAzmgyAyDmFDHda5ljE6CKgRjiqLzJgkneDJp6UGWAxSUMh0CYCZCEVlFnztitgcQdgZSSqLB3LwTGIGYLBOAYzXKk5TkN7on6VEyGVQAnAUAkB4hkC0ALT7BUDoB1L9EJkoFBX8h3ignhUx5vFuxqhHKeTeBCjxLZZaFFkG5gCpXLhuqFXFXtI+HlUhVVUJg1XoaKhdAemgmhCLxtURBAA */
      ...quoteByEmailMachineConfig
    },
    {
      guards
    }
  );
